import { useState } from 'react'

import { Stars } from './Stars'
import { Button, ExpandButton } from '../../Theme'
import { SectionTitle } from '../../UiBlocks'
import { useReviewsActions } from '../hooks'
import { REVIEW_LABELS as LABELS } from '../lib'
import { useReviews } from '../state'

import type { ReviewsActionsValue } from '../hooks'

/**
 * ReviewList - Presentational component for displaying product reviews
 *
 * All business logic (calculations, navigation) is handled by useReviewsActions hook.
 * This component only renders the UI.
 */
export function ReviewList() {
  const { reviews, error } = useReviews()
  const { averageRating, reviewCount, ratingStats, navigateToReviewForm, scrollToReviews } =
    useReviewsActions(reviews)

  const [isExpanded, setIsExpanded] = useState(false)

  if (error || !reviews || reviews.length === 0) {
    return null
  }

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded)
    scrollToReviews()
  }

  return (
    <div className={'react-css relative rounded bg-white md:mb-10 mb-5'} id={'react-reviews'}>
      {/* Header */}
      <div className={'bg-cards-bg text-text-color'}>
        <SectionTitle size="sm" align="left" className="px-10 leading-[50px] mb-0">
          {LABELS.reviews}
        </SectionTitle>
      </div>

      {/* Content */}
      <div
        className={`react-css overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-full' : 'max-h-[600px]'} p-6`}
      >
        {/* Summary Section */}
        <div
          className={
            'react-css border-b-2 mb-8 flex md:flex-row flex-col items-start justify-start md:gap-8 gap-5 pb-5'
          }
        >
          {/* Average Rating */}
          <div className={'react-css flex flex-row items-center justify-start gap-5 '}>
            <p
              className={
                'react-css text-custom-gray font-main-font text-8xl italic font-black leading-none tracking-normal'
              }
            >
              {averageRating}
            </p>
            <div className={'react-css flex flex-col items-start justify-start gap-2'}>
              <span className={'react-css flex flex-row items-center justify-start gap-1 text-custom-gray'}>
                <Stars totalStars={averageRating} starSize={'h-12 w-12'} />
              </span>
              <p
                className={
                  'react-css flex flex-row gap-2 items-center text-custom-gray font-sec-font text-lg font-bold leading-tight tracking-normal'
                }
              >
                <span className={'react-css font-sec-font text-lg font-bold'}>{reviewCount}</span>
                {LABELS.reviews}
              </p>
            </div>
          </div>

          {/* Rating Breakdown & CTA */}
          <div className={'flex flex-row gap-10 xl:gap-5'}>
            <RatingBreakdown ratingStats={ratingStats} />
            <div className={'react-css flex flex-col items-start justify-start gap-3'}>
              <Button
                onClick={navigateToReviewForm}
                variant="expand"
                size="expand"
                type="button"
                className="mt-2 md:mb-0 mb-5"
              >
                {LABELS.writeReview}
              </Button>
            </div>
          </div>
        </div>

        {/* Reviews List */}
        {reviews.map((review) => (
          <div className={'mb-8 border-b-2 flex md:flex-row flex-col'} key={`${review.nickname}-${review.created_at}`}>
            <div className={'flex md:flex-col flex-row gap-3 items-start justify-start md:w-3/12 mr-3 md:mb-0 mb-3'}>
              <span className={'react-css font-sec-font font-bold text-text-color text-xl'}>{review.nickname}</span>
              <span className={'react-css text-xl font-sec-font font-normal'}>{review.created_at}</span>
            </div>
            <div className={'flex flex-col gap-3 md:w-9/12'}>
              <SectionTitle as="h2" size="sm" align="left" className="react-css font-sec-font mb-0 text-xl">
                {review.summary}
              </SectionTitle>
              <div className={'mb-5'}>
                {review.ratings_breakdown.map((rating) => (
                  <div className={'flex flex-row items-center justify-start space-x-8'} key={rating.name}>
                    <div className={'flex flex-row items-center justify-start space-x-1'}>
                      <Stars totalStars={Number(rating.value)} starSize={'h-8 w-8'} />
                    </div>
                  </div>
                ))}
              </div>
              <p className={'react-css font-sec-font text-xl font-normal text-text-color mb-5'}>{review.text}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Gradient Overlay */}
      <div
        className={`react-css absolute top-0 left-0 w-full h-full transition-all duration-300 bg-custom-gradient rounded pointer-events-none ${isExpanded ? 'opacity-0' : 'opacity-100'}`}
      />

      {/* Expand/Collapse Button */}
      <div className={'react-css relative z-10 flex items-center justify-center md:mb-0 mb-5'}>
        <ExpandButton
          isExpanded={isExpanded}
          onClick={handleExpandClick}
          expandedText={LABELS.hideReviews}
          collapsedText={LABELS.showAllReviews}
        />
      </div>
    </div>
  )
}

/**
 * RatingBreakdown - Sub-component for rating statistics
 */
interface RatingBreakdownProps {
  ratingStats: ReviewsActionsValue['ratingStats']
}

function RatingBreakdown({ ratingStats }: RatingBreakdownProps) {
  const ratings = [
    { stars: 5, stats: ratingStats.five },
    { stars: 4, stats: ratingStats.four },
    { stars: 3, stats: ratingStats.three },
    { stars: 2, stats: ratingStats.two },
    { stars: 1, stats: ratingStats.one },
  ]

  return (
    <div className={'react-css flex flex-col items-start justify-start gap-2'}>
      {ratings.map(({ stars, stats }) => (
        <p key={stars} className={'react-css flex flex-row items-center justify-start gap-2'}>
          <span className={'react-css font-sec-font text-lg font-bold'}>{stars} </span>
          <span className={'react-css flex flex-row items-center justify-start gap-1'}>
            <Stars totalStars={stars} starSize={'h-4 w-4'} />
          </span>
          <span className={'react-css font-sec-font text-lg font-bold'}>{stats.percentage}%</span>
          <span className={'react-css font-sec-font text-lg font-bold'}>({stats.voteCount})</span>
        </p>
      ))}
    </div>
  )
}
